"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Logo } from "./Logo";
import { Button } from "./Button";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { type NavLink, LANDING_LINKS, PAGE_LINKS } from "./NavLinks";
import { siteConfig } from "@/config/site";
import { getScriptarioHomePath } from "@/config/locales";
import { landingContent } from "@/content/landing";
import { buildAppUrl } from "@/lib/config";
import { appendUtmToUrl } from "@/lib/utm-forward";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import { useAccountPresence } from "@/hooks/useAccountPresence";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  links?: NavLink[];
  onNavClick?: (label: string) => void;
  onLogin: () => void;
  onStartFree: () => void;
  startFreeLabel?: string;
  /** Use anchor links for Features/Pricing sections */
  isLanding?: boolean;
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M15 5 5 15M5 5l10 10" stroke="rgba(0,0,0,0.9)" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function MobileMenu({ open, onClose, links, onNavClick, onLogin, onStartFree, startFreeLabel, isLanding }: MobileMenuProps) {
  const resolved = links ?? (isLanding ? LANDING_LINKS : PAGE_LINKS);
  const scriptarioLocale = siteConfig.locale === "ru" ? "en" : siteConfig.locale;
  const homeHref = siteConfig.siteId === "scriptario" ? getScriptarioHomePath(scriptarioLocale) : "/";

  const hasAccount = useAccountPresence();

  // Lock body scroll + close on Escape while open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] desktop:hidden">
      {/* Scrim (visible mainly on mobile; harmless on tablet since drawer covers full width) */}
      <div
        className="absolute inset-0 bg-[rgba(0,0,0,0.2)]"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer — full width, full height, anchored left */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={landingContent.header.menu}
        className="absolute inset-y-0 left-0 w-full bg-white rounded-l-[12px] flex flex-col justify-between p-[24px]"
      >
        <div className="flex flex-col gap-[24px]">
          {/* Top row: logo + close */}
          <div className="flex items-center justify-between">
            <Link href={homeHref} onClick={onClose}>
              <Logo />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label={landingContent.header.closeMenu}
              className="flex items-center justify-center cursor-pointer rounded-[12px] p-[10px] border-none bg-transparent transition-colors hover:bg-black/5"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Vertical nav */}
          <nav className="flex flex-col gap-[24px] px-[16px]">
            {resolved.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  onNavClick?.(link.label);
                  const isAnchor = link.href.startsWith("#");
                  if (isAnchor) {
                    // Close first (restores body-scroll), then scroll to the
                    // anchor once the lock is lifted to avoid a race.
                    e.preventDefault();
                    onClose();
                    requestAnimationFrame(() => scrollToAnchor(link.href));
                  } else {
                    onClose();
                  }
                }}
                className="rounded-[12px] px-[8px] py-[6px] transition-colors hover:bg-black/5"
              >
                <span
                  className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[20px] leading-[24px] text-text"
                  style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                >
                  {link.label}
                </span>
              </Link>
            ))}
          </nav>
          <div className="px-[16px]">
            <LanguageSwitcher onSelect={onClose} />
          </div>
        </div>

        {/* Bottom CTA row */}
        <div className="flex items-center gap-[16px] w-full">
          {hasAccount ? (
            <Button
              className="flex-1 min-w-0"
              style={{ flex: "1 1 0%", minWidth: 0 }}
              onClick={() => {
                window.location.href = appendUtmToUrl(buildAppUrl("/transcriptions"));
                onClose();
              }}
            >
              {landingContent.header.dashboard}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="flex-1 min-w-0"
                style={{ flex: "1 1 0%", minWidth: 0 }}
                onClick={() => {
                  onLogin();
                  onClose();
                }}
              >
                {landingContent.header.signIn}
              </Button>
              <Button
                className="flex-1 min-w-0"
                style={{ flex: "1 1 0%", minWidth: 0 }}
                onClick={() => {
                  onStartFree();
                  onClose();
                }}
              >
                {startFreeLabel ?? landingContent.header.startFree}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
